'use client';

import { useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import { decompressFromEncodedURIComponent } from 'lz-string';

import AccountTheme from '@hub/common/src/AccountTheme';
import { THEME_TESTER_ORIGINS } from '@hub/common/src/constants';
import { createGoogleFontsCDNLink } from '@hub/common/src/font-utils';
import { queryFunctions } from '@hub/common/src/query-functions';
import type { Account, AccountThemeConfig, CitOrigin } from '@hub/common/src/types/cit-public-api';

import { useCitOriginClient } from './CitOriginClientProvider';

// the weights getFontWeightsString asks the cdn for, written how document.fonts wants them
const FONT_FACES = ['400', '600', '700', 'italic 400', 'italic 700'];

const FONT_LOAD_TIMEOUT = 3000;

// adds a new style sheet with the new google fonts cdn link created from the newly selected fonts
const loadStylesheet = (href: string) =>
  new Promise<void>((resolve) => {
    if (document.querySelector(`link[rel="stylesheet"][href="${href}"]`)) {
      return resolve();
    }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.onload = link.onerror = () => resolve();

    document.head.appendChild(link);
  });

// a font only downloads once text uses it, so load it before the theme changes
const preloadThemeFonts = async (testTheme: string, citOrigin: CitOrigin, account: Account) => {
  let theme: AccountThemeConfig | undefined;

  try {
    theme = JSON.parse(decompressFromEncodedURIComponent(testTheme) || '{}');
  } catch {
    return;
  }

  const accountTheme = new AccountTheme(citOrigin, account, theme);

  // the same order the layout builds its link in, so react reuses this one instead of adding a second
  const fonts = [accountTheme.bodyFont(), accountTheme.titleFont(), accountTheme.headerFont()];

  // shared by both races, so the whole warm up is capped at one timeout rather than one each
  const deadline = new Promise((resolve) => setTimeout(resolve, FONT_LOAD_TIMEOUT));

  try {
    await Promise.race([loadStylesheet(createGoogleFontsCDNLink(...fonts)), deadline]);

    await Promise.race([
      Promise.all(
        fonts.flatMap((font) =>
          FONT_FACES.map((face) => document.fonts.load(`${face} 1rem "${font}"`))
        )
      ),
      deadline
    ]);
  } catch {
    // warming is best effort, a failure here must not stop the theme from applying
  }
};

// applies a theme posted by the theme tester, swapping the param instead of reloading the page
export default function TestThemeChangeListener({ accountId }: { accountId?: string }) {
  const router = useRouter();

  const citOrigin = useCitOriginClient();

  const latestTheme = useRef<string | null>(null);

  // only the hub pro layout loads the account fonts, so only it asks for the account
  const { data: account } = useQuery({
    queryKey: ['account-details', { accountId: accountId || '', citOrigin }],
    queryFn: queryFunctions.getAccountDetails,
    enabled: !!accountId
  });

  useEffect(() => {
    const onMessage = async (event: MessageEvent) => {
      if (event.source !== window.parent || !THEME_TESTER_ORIGINS.includes(event.origin)) {
        return;
      }

      const testTheme = event.data?.type === 'THEME_UPDATE' ? event.data.testTheme : null;

      if (typeof testTheme !== 'string' || !testTheme) {
        return;
      }

      latestTheme.current = testTheme;

      if (new URL(window.location.href).searchParams.get('testTheme') === testTheme) {
        return;
      }

      if (account) {
        await preloadThemeFonts(testTheme, citOrigin, account);
      }

      if (latestTheme.current !== testTheme) {
        return;
      }

      const url = new URL(window.location.href);
      url.searchParams.set('testTheme', testTheme);

      // has to come first, refresh refetches whatever url the router is holding
      window.history.replaceState(null, '', url);
      router.refresh();
    };

    window.addEventListener('message', onMessage);

    return () => window.removeEventListener('message', onMessage);
  }, [router, citOrigin, account]);

  return null;
}
